/**
 * SENTENCE-LEVEL NARRATIVE REDACTION — portal mirror.
 *
 * Mirrors backend services/strategyNarrativeSentenceRedaction.js (same
 * algorithm, ES module). The backend already serves sentence-redacted text;
 * the portals re-sanitize defensively, and a line-level pass here would undo
 * the backend's precision by deleting a whole paragraph for one sentence —
 * exactly how campaign 53's "Assumptions / Watchouts" and "Budget Allocation"
 * rendered as bare headings.
 *
 * Rules (identical to the backend):
 *   1. a sentence the caller's rule matches is removed;
 *   2. amounts, large numbers and percentages in a removed sentence are
 *      tainted for the rest of its paragraph, in every form they are
 *      restated in ("$11,200", "11200", "$11.2K", "30 percent"), rounded
 *      restatements ("about $11K") included;
 *   3. a sentence that directly follows a removed one and points back at it
 *      ("This…", "It…", "That figure…") is removed;
 *   4. a heading whose whole section was emptied by redaction is removed.
 * A sentence that ends on an abbreviation ("…in the U.S. An internal…")
 * loses only its matching part; the whole unit's figures stay tainted.
 * Pipe-delimited structured rows keep the caller's segment rule.
 */

// Tokens that end in a period without ending a sentence. Compared lowercased,
// without the trailing period.
const NON_TERMINAL_ABBREVIATIONS = new Set([
  "approx",
  "apr",
  "aug",
  "co",
  "corp",
  "dec",
  "dept",
  "dr",
  "e.g",
  "est",
  "etc",
  "feb",
  "fig",
  "i.e",
  "inc",
  "jan",
  "jr",
  "jul",
  "jun",
  "ltd",
  "mar",
  "mr",
  "mrs",
  "ms",
  "no",
  "nov",
  "oct",
  "sep",
  "sept",
  "sr",
  "st",
  "u.k",
  "u.s",
  "u.s.a",
  "vs",
]);

// Of the abbreviations above, those that can also END a sentence. Only these
// open a soft boundary before a capitalised word; the rest never split.
const SENTENCE_FINAL_ABBREVIATIONS = new Set([
  "co",
  "corp",
  "etc",
  "inc",
  "jr",
  "ltd",
  "sr",
  "u.k",
  "u.s",
  "u.s.a",
]);

// A sentence that opens by pointing back at the one before it.
const ANAPHORIC_OPENING =
  /^["'“‘([]*(?:this|that|these|those|it|its|such|the\s+(?:same|figure|amount|number|value|difference|remainder|balance))\b/i;

// A list marker the redaction must keep with the line, not the first sentence.
const LIST_MARKER = /^((?:[-•*·–]|\d{1,2}[.)])\s+)/;

// One figure in any written form: an optional currency sign, a grouped or
// plain number, an optional scale word and an optional percent. The leading
// group stands in for a lookbehind (not every supported browser parses one):
// a digit run inside a word ("Q4", "v2") or a decimal is not a figure.
const FIGURE =
  /(^|[^\w.,])(\$\s?)?(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?(?:\s?(k|mm|m|thousand|million|bn|billion)\b)?(\s?%|\s?(?:per\s?cent|percent)\b)?/gi;

const MULTIPLIERS = Object.freeze({
  k: 1e3,
  thousand: 1e3,
  m: 1e6,
  mm: 1e6,
  million: 1e6,
  bn: 1e9,
  billion: 1e9,
});

function lastTokenOf(candidate) {
  const lastToken = String(candidate || "").trim().split(/\s+/).pop() || "";
  return lastToken
    .replace(/^[("'“‘[]+/, "")
    .replace(/\.$/, "")
    .toLowerCase();
}

function isNonTerminalPeriod(candidate) {
  const trimmed = String(candidate || "").trim();
  if (!trimmed.endsWith(".")) return false;
  const token = lastTokenOf(trimmed);
  if (!token) return false;
  if (NON_TERMINAL_ABBREVIATIONS.has(token)) return true;
  // Initials ("J.") and dotted acronyms ("U.S", "N.Y.C").
  return /^[a-z]$/.test(token) || /^(?:[a-z]\.)+[a-z]$/.test(token);
}

/**
 * Split one narrative line into parts, marking which part only follows a
 * SOFT boundary: an abbreviation that can end a sentence ("U.S.", "etc.")
 * followed by a capitalised word. Every other boundary is hard.
 *
 * Conservative on purpose: a boundary is a terminal mark followed by space and
 * an opening capital, digit, currency sign or quote. Decimals ("1.05"),
 * abbreviations ("U.S. households", "Dec. 1") and clause punctuation (";",
 * ":") never split.
 */
function splitNarrativeSentenceParts(line) {
  const text = String(line || "");
  const parts = [];
  const boundary = /([.!?]+)(["'”’)\]]*)(\s+)(?=["'“‘([]?[A-Z0-9$])/g;
  let partStart = 0;
  let softBefore = false;
  let match;
  while ((match = boundary.exec(text)) !== null) {
    const end = match.index + match[1].length + match[2].length;
    const candidate = text.slice(partStart, end);
    let soft = false;
    if (match[1] === "." && isNonTerminalPeriod(candidate)) {
      const opensSentence = /^["'“‘([]?[A-Z]/.test(text.slice(end + match[3].length));
      if (!opensSentence || !SENTENCE_FINAL_ABBREVIATIONS.has(lastTokenOf(candidate))) continue;
      soft = true;
    }
    if (candidate.trim()) parts.push({ start: partStart, end, softBefore });
    softBefore = soft;
    partStart = end + match[3].length;
  }
  if (text.slice(partStart).trim()) parts.push({ start: partStart, end: text.length, softBefore });
  return parts;
}

/**
 * Split one narrative line into sentences as the author punctuated them: a
 * soft boundary never splits, so "U.S. households" and "…the U.S. An
 * internal…" each stay one sentence.
 */
export function splitNarrativeSentences(line) {
  const text = String(line || "");
  const units = [];
  for (const part of splitNarrativeSentenceParts(text)) {
    if (part.softBefore && units.length) units[units.length - 1].end = part.end;
    else units.push({ start: part.start, end: part.end });
  }
  return units.map((unit) => text.slice(unit.start, unit.end).trim()).filter(Boolean);
}

function parseFigure(match) {
  const [, prefix, currencySign, integerPart, fraction = "", scale = "", percentSign = ""] = match;
  const base = Number(`${integerPart.replace(/,/g, "")}${fraction}`);
  if (!Number.isFinite(base)) return null;
  const decimals = fraction ? fraction.length - 1 : 0;
  if (percentSign) return { kind: "percent", value: base, precision: 10 ** -decimals };
  const multiplier = MULTIPLIERS[scale.toLowerCase()] || 1;
  if (multiplier > 1) {
    return { kind: "amount", value: base * multiplier, precision: 10 ** -decimals * multiplier };
  }
  const isCurrency = Boolean(currencySign) || prefix === "$";
  if (!isCurrency && !integerPart.includes(",")) {
    // A plain number is a figure from four digits up — except a year.
    if (integerPart.length < 4) return null;
    if (integerPart.length === 4 && !fraction && base >= 1900 && base <= 2099) return null;
  }
  return { kind: "amount", value: Math.round(base * 100) / 100, precision: 0 };
}

function collectDisclosedFigures(sentence) {
  const figures = [];
  for (const match of String(sentence || "").matchAll(FIGURE)) {
    const figure = parseFigure(match);
    if (figure) figures.push(figure);
  }
  return figures;
}

function nearlyEqual(left, right) {
  return Math.abs(left - right) <= 1e-9 * Math.max(1, Math.abs(left), Math.abs(right));
}

// A figure written at a coarser precision ("$11K", "30%") repeats every
// figure it rounds from.
function roundsTo(value, figure) {
  return (
    figure.precision > 0 &&
    nearlyEqual(Math.round(value / figure.precision) * figure.precision, figure.value)
  );
}

function figuresMatch(left, right) {
  if (left.kind !== right.kind) return false;
  if (Math.abs(left.value - right.value) < 0.005) return true;
  return roundsTo(left.value, right) || roundsTo(right.value, left);
}

/**
 * One pass: taint every figure in a removed sentence (plus `seedFigures`),
 * then remove any kept sentence that repeats a tainted figure or opens by
 * pointing back at a removed sentence directly before it. Mutates `removed`;
 * returns whether anything new was removed.
 */
function removeTaintedAndBackReferences(sentences, removed, seedFigures = []) {
  const tainted = seedFigures.slice();
  for (let index = 0; index < sentences.length; index += 1) {
    if (removed[index]) tainted.push(...collectDisclosedFigures(sentences[index]));
  }
  let removedAny = false;
  for (let index = 0; index < sentences.length; index += 1) {
    if (removed[index]) continue;
    const followsRemoval = index > 0 && removed[index - 1];
    const repeatsTaint = collectDisclosedFigures(sentences[index]).some((figure) =>
      tainted.some((taint) => figuresMatch(figure, taint))
    );
    if ((followsRemoval && ANAPHORIC_OPENING.test(sentences[index])) || repeatsTaint) {
      removed[index] = true;
      removedAny = true;
    }
  }
  return removedAny;
}

/**
 * Redact one narrative line sentence by sentence.
 *
 * `shouldRedact(sentence)` is the caller's disclosure rule. Returns
 * `{ text, changed }`: `text` is "" when nothing client-safe survives, and
 * `changed` is true whenever any sentence was removed.
 */
export function redactNarrativeLineBySentence(line, shouldRedact) {
  const trimmed = String(line || "").trim();
  if (!trimmed) return { text: "", changed: false };

  const marker = (LIST_MARKER.exec(trimmed) || [""])[0];
  const body = trimmed.slice(marker.length);
  const parts = splitNarrativeSentenceParts(body);
  const partTexts = parts.map((part) => body.slice(part.start, part.end).trim());
  const units = [];
  parts.forEach((part, index) => {
    if (part.softBefore && units.length) units[units.length - 1].push(index);
    else units.push([index]);
  });

  const removed = parts.map(() => false);
  const seedFigures = [];
  for (const unit of units) {
    const unitText = body.slice(parts[unit[0]].start, parts[unit[unit.length - 1]].end).trim();
    if (!shouldRedact(unitText)) continue;
    if (unit.length === 1) {
      removed[unit[0]] = true;
      continue;
    }
    // A unit joined across a sentence-final abbreviation: remove the parts
    // the rule matches (all of them when no part matches alone) and keep the
    // whole unit's figures tainted.
    const matching = unit.filter((index) => shouldRedact(partTexts[index]));
    (matching.length ? matching : unit).forEach((index) => {
      removed[index] = true;
    });
    seedFigures.push(...collectDisclosedFigures(unitText));
  }
  if (!removed.some(Boolean)) return { text: trimmed, changed: false };

  /*
   * Iterate to a fixed point: a sentence removed for repeating a tainted
   * figure taints the figures it carried too, and a back-referencing sentence
   * after ANY removed sentence goes with it.
   */
  let removedMore;
  do {
    removedMore = removeTaintedAndBackReferences(partTexts, removed, seedFigures);
  } while (removedMore);

  const kept = partTexts.filter((_, index) => !removed[index]);
  if (!kept.length) return { text: "", changed: true };
  return { text: `${marker}${kept.join(" ")}`, changed: true };
}

/**
 * Is this line a section heading rather than body copy?
 *
 * Sherloc headings are short, unpunctuated title lines ("Budget Allocation",
 * "Assumptions / Watchouts"). Anything that ends like a sentence, carries a
 * structured row, a list marker or a dollar figure is body copy.
 */
export function isNarrativeHeadingLine(line) {
  const text = String(line || "").trim();
  if (!text || text.length > 80) return false;
  if (/[.!?;:,]$/.test(text)) return false;
  if (text.includes("|")) return false;
  if (LIST_MARKER.test(text)) return false;
  if (/\$\s?\d/.test(text)) return false;
  if (!/[A-Za-z]/.test(text)) return false;
  return text.split(/\s+/).length <= 8;
}

/**
 * Remove headings whose whole section was emptied by redaction.
 *
 * `entries` is one `{ text, removed }` per original line, in order: `text` is
 * what survived ("" when nothing did) and `removed` is true only for a line
 * that HAD content and lost all of it. Genuine blank lines are neither. A
 * heading is dropped only when at least one line of its section was removed
 * and nothing of that section survived — a heading that was always followed
 * directly by another heading is left alone. Walked from the end so a nested
 * heading emptied first also empties its parent.
 */
export function dropHeadingsEmptiedByRedaction(entries) {
  const out = (Array.isArray(entries) ? entries : []).map((entry) => ({
    text: String(entry?.text || ""),
    removed: Boolean(entry?.removed),
  }));
  for (let index = out.length - 1; index >= 0; index -= 1) {
    const entry = out[index];
    if (!entry.text || entry.removed || !isNarrativeHeadingLine(entry.text)) continue;
    let sawRemoved = false;
    let sawContent = false;
    for (let next = index + 1; next < out.length; next += 1) {
      const following = out[next];
      if (following.removed) {
        sawRemoved = true;
        continue;
      }
      if (!following.text) continue;
      if (!isNarrativeHeadingLine(following.text)) sawContent = true;
      break;
    }
    if (sawRemoved && !sawContent) {
      entry.text = "";
      entry.removed = true;
    }
  }
  return out;
}
